import React, { useCallback, useMemo, useRef, useState, memo, useEffect } from 'react';
import { TextField, Typography } from '@mui/material';
import { LocationFetch } from '../../../library/LocationFetch';
import { IfscFetch } from '../../../library/IfscFetch';
import { showErrorSnackbar, showWarningSnackbar } from '../../../components/snackBar/snakbar';

const FormikTextField = memo(
  React.forwardRef(function FormikTextField(
    {
      formik,
      name,
      label,
      rows,
      resetbool, // kept for API compatibility (not used without effects)
      cityref,
      disabled,
      placeholder,
      handlePan, // kept for compatibility
      district = false,
      id,
      viewPage,
      states = [],
      setGstApi = () => {},
      upiChange = () => {},
      maxLength = 50,
      setOptions = () => {},
    },
    ref
  ) {
    // ---- Local editing buffer (no effects) ----
    const [value, setValue] = useState(formik.values?.[name] ?? '');
    const [editing, setEditing] = useState(false);
    const [paste, setPaste] = useState(false);

    // Debounce timer (no effects)
    const debounceRef = useRef(null);
    useEffect(() => {
      setValue('');
    }, [resetbool]);
    const scheduleSet = useCallback((fn, delay = 400) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fn();

        debounceRef.current = null;
      }, delay);
    }, []);

    // Displayed value comes from local while editing, otherwise from Formik
    const baseDisplayValue = editing ? value : formik.values?.[name] ?? '';
    const displayValue = baseDisplayValue;

    const setTouched = useCallback(() => {
      formik.setFieldTouched(name, true, false);
    }, [name]);

    const handleChange = useCallback(
      (event) => {
        const raw = event.target.value;

        // PAN
        if (name === 'pan') {
          const up = raw.toUpperCase().trim();
          setEditing(true);
          setValue(up);
          if (raw.length === 10 || raw.length === 9 || raw.length < 2) {
            formik.setFieldValue(name, up);
            setTouched();
            formik.validateField(name);
          }
          return;
        }
        console.log(name);
        if (name === 'legalName') {
          // Allow spaces while typing; capitalize first letter of each word; hard-limit 50 chars
          const limited = raw.slice(0, 50);
          const capitalized = limited ? limited.replace(/\b\w/g, (l) => l.toUpperCase()) : '';
          setEditing(true);
          setValue(capitalized);
          formik.setFieldValue('legalShortName', capitalized);
          formik.setFieldValue('legalName', capitalized);
          formik.setFieldTouched('legalShortName', false);
          return;
        }
        if (name === 'legalShortName') {
          // Allow spaces while typing; capitalize first letter of each word; hard-limit 50 chars
          const limited = raw.slice(0, 50);
          const capitalized = limited ? limited.replace(/\b\w/g, (l) => l.toUpperCase()) : '';
          setEditing(true);
          setValue(capitalized);
          formik.setFieldValue('legalShortName', capitalized);
          return;
        }
        if (name === 'ConsigneeName' || name === 'fullname' || name === 'Designation') {
          const limited = raw.slice(0, 50);
          const capitalized = limited ? limited.replace(/\b\w/g, (l) => l.toUpperCase()) : '';
          setEditing(true);
          setValue(capitalized);
          formik.setFieldValue(name, capitalized);
          formik.setFieldTouched(name, false);
          return;
        }
        // GSTIN / ConsigneeGSTIN
        if (name === 'gstin' || name === 'ConsigneeGSTIN') {
          const up = raw.toUpperCase().trim();
          setEditing(true);
          setValue(up);
          if (name === 'gstin' && up.length === 15) setGstApi(true);

          if (up.length === 15 || up.length === 14 || up.length < 3) {
            formik.setFieldValue(name, up);
            setTouched();
            formik.validateField(name);
          }
          return;
        }

        // Email
        if (name === 'email' || name === 'EmailContact') {
          const v = raw.trim();
          setEditing(true);
          setValue(v);
          if (v.length < 2 || (v.includes('@') && v.includes('.'))) {
            scheduleSet(() => formik.setFieldValue(name, v));
          }
          return;
        }

        // Multiline / notes
        if (rows) {
          if (raw.length <= 130) {
            setEditing(true);
            setValue(raw);
            if (raw.length <= 1) {
              formik.setFieldValue(name, raw);
            } else {
              scheduleSet(() => formik.setFieldValue(name, raw));
            }
          }
          return;
        }

        // Default
        if (raw.length <= 100) {
          setEditing(true);
          setValue(raw);

          if (name === 'upi') {
            upiChange(raw);
            formik.setFieldValue(name, raw); // immediate write for UPI
            return;
          }

          if (paste === true || name === 'bankAC' || raw.length <= 1) {
            formik.setFieldValue(name, raw);
          } else {
            scheduleSet(() => formik.setFieldValue(name, raw));
          }
        }
      },
      [name, rows, paste, setGstApi, upiChange, scheduleSet, setTouched]
    );

    const handleIfsc = useCallback(
      (e) => {
        const val = e.target.value;
        if (val.length <= 25) {
          const up = val.toUpperCase().trim();
          setEditing(true);
          setValue(val);

          formik.setFieldValue(name, up);
          setTouched();
          formik.validateField(name);

          if (up.length === 11) {
            IfscFetch(up)
              .then((res) => {
                formik.setValues({
                  ...formik.values,
                  ifsc: up,
                  branch: res.data.branchName,
                  bankName: res.data.bankName,
                  swiftcode: res.data.swift,
                });
              })
              .catch(() => {
                showErrorSnackbar('IFSC not found in our database');
              });
          }
        }
      },
      [formik, name, setTouched]
    );

    const handleKeyDown = useCallback(
      (e, fieldName) => {
        if (e.key === 'Tab' || e.key === 'Enter') {
          const fieldError = formik.errors?.[fieldName];
          const fieldTouched = formik.touched?.[fieldName];

          if (fieldError && fieldTouched) {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      },
      [formik.errors, formik.touched]
    );

    const inputPropsMemo = useMemo(() => {
      if (name === 'billingAddress' || name === 'billingAddressD') {
        return {
          style: { alignItems: 'flex-start', letterSpacing: 'normal' },
          classes: { input: 'custom-scrollbar' },
        };
      }
      return { classes: { input: 'custom-scrollbar' } };
    }, [name]);

    const inputLabelPropsMemo = useMemo(() => {
      if (name === 'notes') return { shrink: Boolean(displayValue) };
      if (name === 'upi') return { shrink: true };
      return undefined;
    }, [name, displayValue]);

    const onBlur = useCallback(() => {
      // Flush pending change on blur and stop editing
      if ((formik.values?.[name] ?? '') !== value) {
        formik.setFieldValue(name, value);
      }
      // setTouched();
      setEditing(false);
      setPaste(false);
      formik.setFieldTouched(name, true);
    }, [formik, name, value]);

    const onFocus = useCallback(() => {
      setEditing(true);
      const fv = formik.values?.[name] ?? '';
      setValue(fv === 0 || fv === '0' ? '' : fv);
    }, [formik, name]);

    return (
      <>
        <TextField
          size="small"
          id={name}
          name={name}
          value={displayValue}
          fullWidth
          placeholder={placeholder}
          error={Boolean(formik.touched?.[name] && formik.errors?.[name] && !formik.values?.[name])}
          label={label}
          InputLabelProps={inputLabelPropsMemo}
          sx={{
            fontFamily: 'overpass',
            '.MuiFormLabel-asterisk': {
              color: 'danger.main',
              fontSize: '20px',
              fontWeight: 'bold',
            },
          }}
          required={Boolean(formik.errors?.[name] && !formik.values?.[name])}
          disabled={viewPage || disabled}
          onFocus={onFocus}
          onBlur={onBlur}
          multiline={Boolean(rows && rows > 2)}
          rows={rows || undefined}
          inputRef={ref}
          autoComplete="off"
          onKeyDown={(e) => handleKeyDown(e, name)}
          onChange={
            name === 'ifsc'
              ? handleIfsc
              : // : name === 'Pincode'
                // ? (e) => handlePincode(e, 'State', 'District', 'Country')
                // : name === 'PincodeD'
                // ? (e) => handlePincode(e, 'StateD', 'DistrictD', 'CountryD')
                handleChange
          }
          onPaste={() => setPaste(true)}
          InputProps={inputPropsMemo}
          inputProps={{ maxLength }}
        />

        {formik.touched?.[name] && formik.errors?.[name] ? (
          <Typography variant="body1" className="error" style={{ fontSize: 12, padding: 0 }}>
            {formik.errors[name]}
          </Typography>
        ) : (
          <p style={{ padding: 9 }} />
        )}
      </>
    );
  })
);

export default FormikTextField;
